import React, { useState, useMemo, useEffect, useRef } from 'react';

/**
 * PropertyModule — 7-step wizard chrome for the admin Property tab.
 *
 * This component owns only the header (title + close button), the
 * 7-circle progress bar, and the per-step navigation footer (Back /
 * Next). The actual step body is supplied by AdminBorrowerProfile
 * via the `renderStep(stepNum)` render prop, which lets each step
 * keep using the existing inline form state (offerTerms, editLaForm,
 * disclosureForm, hcLoading, etc.) without prop-drilling every
 * setter through this component.
 *
 * SmartResume: on mount, jumps to deriveCurrentStep(...) — the
 * lowest step that's not yet complete. Admin can use the progress
 * bar to navigate back to any completed step.
 *
 * deriveCurrentStep is exported so the parent's CTA button can
 * show "Resume — Step N of 7" without duplicating the logic.
 */

export const STEPS = [
  // Step 1 — Seller's Disclosure. The old Step 1 ("Listing Agent"
  // data-entry surface) is removed: LA contact info auto-populates
  // from the MLS / SimplyRETS feed at offer-request time and surfaces
  // as a permanent "Listing Agent" card at the top of the Property
  // tab (with an Edit override) — outside the module so the admin
  // doesn't have to step into the wizard just to view it. The
  // module now starts at the first thing requiring admin action.
  { id: 'disclosure',     shortLabel: 'Disclosure',    label: "Seller's Disclosure" },
  { id: 'valuation',      shortLabel: 'Valuation',     label: 'Valuation' },
  // Step 3 — Underwriting Review. Discrete gate before the offer
  // gets routed to the listing agent. Done when every review section
  // is accepted on the Review tab (reviewComplete=true). The legacy
  // !reviewComplete blocker that disabled the Generate Offer PDF
  // button has been promoted to its own step so the dependency is
  // visible in the step chrome instead of buried as a button
  // disabled-tooltip.
  { id: 'uw_review',      shortLabel: 'UW Review',     label: 'Underwriting Review' },
  { id: 'offer_details',  shortLabel: 'Offer',         label: 'Offer Details' },
  // Entering terms + generating + routing all happen on the one
  // 'Offer Details' surface.
  { id: 'accept',         shortLabel: 'Accept',        label: 'Accept' },
  { id: 'execution',      shortLabel: 'Sign',          label: 'Contract Execution' },
  { id: 'bc_psa',         shortLabel: 'Borrower',      label: 'B-C PSA' },
  // RE desk captures property access + availability for the
  // inspection/appraisal scheduling (Inspectify). Unlocked once the
  // B-C PSA is executed (prior step done).
  { id: 'inspectify_access', shortLabel: 'Access',     label: 'Inspection & Appraisal Access' },
];

// Pure derivation — reads from existing data state, no DB column.
// Returns 1..8 (lowest incomplete step) or 'complete' when all done.
export function deriveCurrentStep({
  user, offerRequest, disclosureData, documents, loanStage, optionPeriod,
  // A-B PSA execution state for the Step-7 gate. Shape:
  //   { hasPackage: bool, status: string|null, allSignedAt: ts|null }
  // In-app signing path: that step is done only when Doorly has countersigned
  // (package status 'completed' / all_signed_at set) — NOT when sellers merely
  // signed (a psa_ab_signed doc exists at that point but the contract is not
  // yet fully executed). Manual-upload path (no in-app package): the uploaded
  // psa_ab_signed doc IS the executed contract. Omitting abPsa preserves the
  // legacy doc-existence behavior.
  abPsa,
  // Underwriting Review acceptance flag. Sourced from the
  // borrower-detail response's reviewComplete field (every section
  // accepted by the underwriter on the Review tab). When omitted,
  // this gate fails-OPEN so legacy callers that don't pass it
  // through aren't blocked at the new Step 4.
  reviewComplete = true,
}) {
  // Step 1: Disclosure submitted OR new construction. LA contact info
  // is no longer a gating step — it auto-populates from MLS at
  // offer-request time and surfaces on the always-on Listing Agent
  // card at the top of the Property tab.
  const disclosureSubmitted =
    disclosureData?.disclosure?.status === 'submitted'
 || disclosureData?.disclosure?.status === 'received';
  const disclosureDone =
    !!offerRequest?.is_new_construction || disclosureSubmitted;
  if (!disclosureDone) return 1;

  // Step 2: Valuation run AND succeeded. Previously this OR'd
  // hc_analyzed_at into the done-check, but that timestamp gets set
  // on every HC attempt — including partial/error runs where the
  // AVM came back null. Result: a failed HC pull silently completed
  // step 2 and unlocked step 3 without the admin having any actual
  // valuation to work from. Tightening to hc_avm_value forces the
  // step to stay open until a real AVM number lands.
  const valuationDone = !!user?.hc_avm_value;
  if (!valuationDone) return 2;

  // Step 3 (NEW): Underwriting Review must be complete before the
  // offer is generated/routed to the listing agent. reviewComplete
  // is the data-derivation flag from /borrowers/:id (every review
  // section accepted by the underwriter on the Review tab). The
  // POST_OFFER_STAGES check below also gates this step done — by
  // the time the file has reached offer_submitted/accepted/etc,
  // the underwriting review is implicitly complete (the offer
  // couldn't have been sent otherwise). That lets the module stay
  // forward-only for files that were submitted before this gate
  // existed.
  const POST_OFFER_STAGES = [
    'offer_submitted', 'offer_accepted', 'under_contract',
    'option_period', 'clear_to_close', 'closing', 'closed', 'funded',
  ];
  const reviewDone = !!reviewComplete || POST_OFFER_STAGES.includes(loanStage);
  if (!reviewDone) return 3;

  // Step 4: Offer Details — borrower's offer has been generated +
  // routed to the listing agent. Done when /send-offer fired
  // (offer_filename set + application_status='offer_submitted') OR
  // the admin used the manual-accept shortcut (manual_accept_offer_at
  // set), OR the loan has clearly moved past the submit phase.
  const offerSent = !!(
    offerRequest?.offer_filename
 || offerRequest?.manual_accept_offer_at
 || POST_OFFER_STAGES.includes(loanStage)
  );
  if (!offerSent) return 4;

  // Step 5: Accept — Listing Agent accepted via the OfferReview
  // portal (application_status flips to offer_accepted) OR admin
  // clicked Manually Accept Offer (manual_accept_offer_at + a
  // matching status advance). Both paths land at offer_accepted;
  // either signal closes the gate. Step 6 (Contract Execution)
  // stays locked until this fires.
  const POST_ACCEPT_STAGES = [
    'offer_accepted', 'under_contract',
    'option_period', 'clear_to_close', 'closing', 'closed', 'funded',
  ];
  const offerAccepted = !!(
    offerRequest?.manual_accept_offer_at
 || POST_ACCEPT_STAGES.includes(loanStage)
  );
  if (!offerAccepted) return 5;

  // Scope PSA docs to the CURRENT offer/property (the latest offer_request).
  // A leftover PSA tagged to a prior offer must not make a fresh property
  // module think Step 6/7 is already done. Pre-tag legacy rows (offer_request_id
  // NULL) are treated as not-belonging once we know the current offer id.
  const currentOfferId = offerRequest?.id ?? null;
  const psaDocs = currentOfferId != null
    ? (documents || []).filter(d => d.offer_request_id === currentOfferId)
    : (documents || []);

  // Step 6: A-B PSA FULLY EXECUTED (Doorly countersigned) — not just
  // sellers-signed. The psa_ab_signed doc exists once sellers sign, so doc
  // existence alone is not "executed". In-app path requires the package to be
  // completed; manual-upload path (no package) trusts the uploaded doc.
  const abHasDoc = !!psaDocs.some(
    d => d.document_type === 'psa_ab_signed'
  );
  const abFullyExecuted = abPsa?.hasPackage
    ? (abPsa.status === 'completed' || !!abPsa.allSignedAt)
    : abHasDoc;
  if (!abFullyExecuted) return 6;

  // Step 7: B-C PSA FULLY executed — Doorly has countersigned. Mirrors the
  // Step 6 gate: borrower-only signing is NOT execution. `borrower_psa_signed`
  // exists the moment the borrower completes the canvas flow (well before
  // Doorly countersigns), so doc-existence alone over-advances this step. Trust
  // either the post-countersign `borrower_psa_fully_executed` artifact OR the
  // option_periods.psa_signed_doorly_at timestamp — both are written only
  // after Jesse actually countersigns via the token flow.
  const bcFullyExecuted = !!psaDocs.some(d => d.document_type === 'borrower_psa_fully_executed')
                       || !!optionPeriod?.psa_signed_doorly_at;
  if (!bcFullyExecuted) return 7;

  // Step 8: Inspection & Appraisal Access captured — done once the RE desk
  // has saved an availability type (the Step 8 save always sets it).
  const accessCaptured = !!optionPeriod?.inspectify_availability_type;
  if (!accessCaptured) return 8;

  return 'complete';
}

export default function PropertyModule({
  // Data the chrome needs
  user,
  offerRequest,
  disclosureData,
  documents,
  loanStage,
  optionPeriod,
  propertyAddress,
  // A-B PSA execution state for the Step-7 gate (see deriveCurrentStep).
  abPsa,
  // Underwriting Review acceptance flag — gates the new Step 4. When
  // omitted, the gate fails-OPEN (legacy callers stay un-blocked).
  reviewComplete,
  // Render-prop for step body
  renderStep,
  // Close the wizard (collapses back to CTA button)
  onClose,
  // Optional: render a "step N can advance now" gate. Receives stepNum,
  // returns boolean. Used to disable Next when step's requirements
  // aren't met. Defaults to step-derivation comparison.
  canAdvance,
}) {
  const derivedStep = useMemo(
    () => deriveCurrentStep({
      user, offerRequest, disclosureData, documents, loanStage, optionPeriod, abPsa, reviewComplete,
    }),
    [user, offerRequest, disclosureData, documents, loanStage, optionPeriod, abPsa, reviewComplete]
  );

  // SmartResume — open on the lowest incomplete step. If everything
  // is done, open on the last step (9) so admin can review. (Last
  // step bumped from 7 → 9 after the UW Review insertion.)
  const initialStep = derivedStep === 'complete' ? STEPS.length : derivedStep;
  const [activeStep, setActiveStep] = useState(initialStep);

  // Auto-advance on data shift is OFF for steps 1–5 — those are
  // admin-driven (Run on Step 2, completing UW review on Step 3,
  // entering price on Step 4, etc) and bumping the step out from
  // under the admin mid-action is disorienting.
  //
  // EXCEPTION: A-B PSA execution step (step 6) → B-C PSA step
  // (step 7). Step 6's done signal flips when sellers + Doorly all
  // finish the A-B PSA — none of which is an in-tab admin action.
  // The admin is waiting on external events, not editing anything,
  // so auto-advancing to the B-C PSA surface when the A-B PSA
  // finalizes is the right UX. Scrolls to the top for the same
  // reason handleNext does.
  //
  // EDGE-TRIGGERED: fire only when derivedStep *crosses* from ≤6 to
  // >6 (the A-B PSA just finalized), not as a standing condition.
  // Otherwise, with the A-B PSA already signed (derivedStep stuck at
  // 7), clicking Back from step 7 → 6 would be instantly bounced
  // forward again, making the Back button look broken.
  const prevDerivedRef = useRef(derivedStep);
  useEffect(() => {
    const prev = prevDerivedRef.current;
    prevDerivedRef.current = derivedStep;
    // ── Auto-advance: A-B PSA execution (step 6 → 7) ─────────────────
    const wasAtOrBefore6 = !(typeof prev === 'number' && prev > 6);
    if (
      activeStep === 6 &&
      typeof derivedStep === 'number' && derivedStep > 6 &&
      wasAtOrBefore6
    ) {
      setActiveStep(7);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // ── Auto-advance: Underwriting Review completes (step 3 → 4) ─────
    // Same edge-trigger pattern as the A-B PSA auto-advance above. When
    // the Loan Team flips reviewComplete to true while the admin is
    // viewing Step 3, fast-forward to Step 4 (Offer Details) so the
    // RE Desk doesn't have to manually click Next on a now-satisfied
    // gate. Edge-triggered so clicking Back from Step 4 → 3 isn't
    // instantly bounced forward again. Mirrors the wasAtOrBefore6
    // guard exactly.
    const wasAtOrBefore3 = !(typeof prev === 'number' && prev > 3);
    if (
      activeStep === 3 &&
      typeof derivedStep === 'number' && derivedStep > 3 &&
      wasAtOrBefore3
    ) {
      setActiveStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep, derivedStep]);

  // numericDerived is the lowest still-incomplete step number, or
  // STEPS.length+1 (10 after the UW Review insertion) when every
  // step is complete — used by isStepDone to flag a step as a
  // green check.
  const numericDerived = derivedStep === 'complete' ? STEPS.length + 1 : derivedStep;
  const isStepDone = (stepNum) => stepNum < numericDerived;

  const handleNext = () => {
    if (activeStep < STEPS.length) {
      setActiveStep(activeStep + 1);
      // Scroll the wrapping page back to the top so the admin lands
      // on the new step's header instead of mid-scroll wherever they
      // were reading on the prior step. Smooth scroll matches the
      // codebase pattern (ApplicationForm / PreQualification /
      // SmartStart all use the same behavior on step advance).
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const handleBack = () => {
    if (activeStep > 1) setActiveStep(activeStep - 1);
  };

  const nextDisabled = (() => {
    if (activeStep >= STEPS.length) return true;
    if (canAdvance) return !canAdvance(activeStep);
    // Default gate: step is done OR user is reviewing a past step
    return !(isStepDone(activeStep) || activeStep < numericDerived);
  })();

  // Per-step "what's still missing" hint. Surfaced as the Next button's
  // tooltip when nextDisabled — generic "Complete this step" copy was
  // unhelpful when an admin couldn't tell which field was holding things
  // up. The strings here mirror exactly what deriveCurrentStep checks
  // for the matching step, so the hint == the gate.
  const NEXT_DISABLED_HINT = {
    1: 'Mark the Seller\'s Disclosure as submitted/received (or flag the listing as new construction) before continuing.',
    2: 'Run the HouseCanary valuation and wait for an AVM value to land before continuing.',
    3: 'Accept every section on the Review tab — Underwriting must be complete before generating the offer.',
    4: 'Generate + send the offer (or use Manual Accept) before continuing.',
    5: 'Wait for the Listing Agent to accept — or click Manually Accept Offer — before continuing.',
    6: 'Wait for the A-B PSA to be fully executed before continuing.',
    7: 'Wait for the B-C PSA to be fully executed.',
    8: 'Set the property access + availability and save before completing.',
  };

  return (
    <div style={{ width: '100%' }}>
      {/* ── Header: dark gradient, title + progress ─────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #111827 0%, #1e3a5f 100%)',
        border: '1px solid #3a6ad9',
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0',
        padding: '18px 22px',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 18, gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#acf44e',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              Property Module
            </div>
            <div style={{
              fontSize: 15, fontWeight: 600, color: '#fff',
              overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {propertyAddress || 'Property Address'}
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none', borderRadius: 8,
                color: '#fff', padding: '6px 12px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              ✕ Close
            </button>
          )}
        </div>

        {/* ── 7-step progress bar ─────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {STEPS.map((step, i) => {
            const stepNum  = i + 1;
            const done     = isStepDone(stepNum);
            const active   = stepNum === activeStep;
            const navigable = done || active || stepNum < numericDerived;
            return (
              <React.Fragment key={step.id}>
                <div
                  onClick={() => navigable && setActiveStep(stepNum)}
                  style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', flex: 1,
                    cursor: navigable ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: done ? '#acf44e'
                              : active ? '#3a6ad9'
                              : 'rgba(255,255,255,0.15)',
                    color: done ? '#111827' : '#fff',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12, fontWeight: 800,
                    marginBottom: 5,
                    border: active ? '2px solid #acf44e' : '2px solid transparent',
                    transition: 'all 120ms ease',
                  }}>
                    {done ? '✓' : stepNum}
                  </div>
                  <div style={{
                    fontSize: 9, lineHeight: 1.2,
                    color: active ? '#acf44e'
                         : done ? 'rgba(255,255,255,0.85)'
                         : 'rgba(255,255,255,0.45)',
                    fontWeight: active ? 700 : 500,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}>
                    {step.shortLabel}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{
                    height: 2, flex: 0.4,
                    background: stepNum < numericDerived
                      ? '#acf44e' : 'rgba(255,255,255,0.15)',
                    marginTop: 13,
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Step body ────────────────────────────────────────────── */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #3a6ad9',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '24px',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#9ca3af',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 4,
        }}>
          Step {activeStep} of {STEPS.length}
        </div>
        <div style={{
          fontSize: 18, fontWeight: 800, color: '#111827',
          marginBottom: 18,
        }}>
          {STEPS[activeStep - 1]?.label}
        </div>

        {/* Step body — delegated to parent so it can use its existing
            local state without prop-drilling every setter through. */}
        <div>
          {renderStep ? renderStep(activeStep) : null}
        </div>

        {/* ── Navigation footer ────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          marginTop: 24, paddingTop: 16,
          borderTop: '1px solid #e5e7eb',
        }}>
          <button
            onClick={handleBack}
            disabled={activeStep <= 1}
            style={{
              background: '#fff', color: activeStep <= 1 ? '#9ca3af' : '#374151',
              border: '1px solid #d1d5db', borderRadius: 8,
              padding: '8px 16px', fontSize: 13, fontWeight: 600,
              cursor: activeStep <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            ← Back
          </button>
          {activeStep < STEPS.length ? (
            <button
              onClick={handleNext}
              disabled={nextDisabled}
              title={nextDisabled
                ? (NEXT_DISABLED_HINT[activeStep] || 'Complete this step before continuing')
                : ''}
              style={{
                background: nextDisabled ? '#e5e7eb' : '#acf44e',
                color: nextDisabled ? '#9ca3af' : '#1a1a1a',
                border: 'none', borderRadius: 8,
                padding: '8px 18px', fontSize: 13, fontWeight: 700,
                cursor: nextDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              Next: {STEPS[activeStep]?.label} →
            </button>
          ) : (
            derivedStep === 'complete' ? (
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#16a34a',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                ✓ All 7 steps complete
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Awaiting B-C PSA execution…
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
